// What is still owed on a prescription line (docs/plans/phase1-dispensing.md §6.1).
//
// The remaining quantity is derived, never stored: prescribed less dispensed, prepared and
// closed. No dose arithmetic happens here; the prescribed quantity comes from the
// prescribing slice in whole base units (stock decision 2).

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import { assertWholeQuantity } from '../stock/quantity.ts';
import type { PrescriptionLineStatus } from './prescription.ts';

export interface LineQuantities {
  readonly prescribed: number;
  readonly dispensed: number;
  readonly prepared: number;
  readonly closed: number;
}

export type BalanceErrorCode = 'pharmacy.balance_inconsistent';

function assertNonNegativeWhole(value: number, label: string): void {
  assertWholeQuantity(value, label);
  if (value < 0) throw new RangeError(`${label} cannot be negative`);
}

/** What is still to dispense or close. Refuses figures that would make it negative. */
export function remainingOnLine(q: LineQuantities): Result<number, BalanceErrorCode> {
  assertNonNegativeWhole(q.prescribed, 'prescribed');
  assertNonNegativeWhole(q.dispensed, 'dispensed');
  assertNonNegativeWhole(q.prepared, 'prepared');
  assertNonNegativeWhole(q.closed, 'closed');
  const remaining = q.prescribed - q.dispensed - q.prepared - q.closed;
  if (remaining < 0) return err('pharmacy.balance_inconsistent');
  return ok(remaining);
}

export const LINE_DISPENSE_STATES = [
  'new',
  'prepared',
  'part_dispensed',
  'dispensed',
  'closed',
  'stopped',
] as const;
export type LineDispenseState = (typeof LINE_DISPENSE_STATES)[number];

/** The line's state in the pharmacy, for the queue and the work view. */
export function lineDispenseState(
  q: LineQuantities & { readonly lineStatus: PrescriptionLineStatus },
): LineDispenseState {
  if (q.lineStatus !== 'active') return 'stopped';
  if (q.prepared > 0) return 'prepared';
  const remaining = q.prescribed - q.dispensed - q.closed;
  if (remaining > 0) return q.dispensed > 0 ? 'part_dispensed' : 'new';
  if (q.closed > 0) return 'closed';
  return 'dispensed';
}

export type SuggestionWarning = 'short' | 'out_of_stock' | 'prepared_for_others';

export interface DispenseSuggestion {
  readonly quantity: number;
  /** How much of what is owed the shelf can't cover now. */
  readonly shortBy: number;
  readonly warning: SuggestionWarning | null;
}

/**
 * The quantity to offer in the "Give" field: never more than is owed, and no more than is
 * usable in the store. Stock prepared for other patients is not reserved (plan §2.3); it
 * only produces a warning when giving this quantity would eat into it.
 */
export function suggestDispenseQuantity(input: {
  readonly remaining: number;
  readonly usable: number;
  readonly preparedForOthers: number;
}): DispenseSuggestion {
  assertNonNegativeWhole(input.remaining, 'remaining');
  assertNonNegativeWhole(input.usable, 'usable');
  assertNonNegativeWhole(input.preparedForOthers, 'preparedForOthers');
  if (input.remaining === 0) return { quantity: 0, shortBy: 0, warning: null };
  if (input.usable === 0) return { quantity: 0, shortBy: input.remaining, warning: 'out_of_stock' };
  const quantity = Math.min(input.remaining, input.usable);
  const shortBy = input.remaining - quantity;
  if (shortBy > 0) return { quantity, shortBy, warning: 'short' };
  if (input.preparedForOthers > 0 && quantity > input.usable - input.preparedForOthers)
    return { quantity, shortBy: 0, warning: 'prepared_for_others' };
  return { quantity, shortBy: 0, warning: null };
}
